import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { Box, Grid } from '@material-ui/core';
import cn from 'classnames';
import useDebounce from '../../hooks/useDebounce';
import Loader from '../../components/Loaders/Loader';
import {
  getSuggestions,
  getDraftTravelContractAndSaveInCache,
  setPaymentMethod,
  setHasAgreed,
  clearTravelContract,
  setOrUpdateSigner,
  clearSignerBackendErrors,
  saveDraftContract,
  addTraveler,
  removeTraveler,
  setHasAddedSigner,
  selectSuggestions,
  saveTraveler,
  removeTravelerBackendErrors,
  setPromoCodeData,
  getMobileContractData,
} from '../../redux/modules/travelContract';
import { getPrices, clearCalculation, clearPrices } from '../../redux/modules/travelCalculation';
import { getSavedCards } from '../../redux/modules/payment';
import {
  ACTION_NAME,
  CONTRACT_TYPES,
  MEMBER_TYPES,
  TRAVEL_CONTRACT_DAYS_DEBOUNCE,
} from '../../constants';
import {
  TRAVEL_CONTRACT_SIGN_PATH,
} from '../../constants/routes';
import EfesContentHeader from '../../components/EfesContentHeader';
import {
  getActiveCoveragesCount,
  getDiscountedPrice,
  getContractSummary,
  getPlansWithAllowedLimitRegionAndAge,
  getSelectedPlansWithAllowedLimitRegionAndAge,
  getOnlyLiteDisallowedCountry,
  getSelectedRegionForSummaryContract,
} from '../../helpers/travel/travelCalcHelpers';
import ContractSummary from './ContractSummary';
import contractSteps, { stepsId } from './contractSteps';
import useDeviceResize from '../../hooks/useDeviceResize';
import { getDiscountPrice } from '../../helpers/price';
import TravelersDialog from '../../components/Dialogs/TravelersDialog';
import AdditionalCoveragesDialog from '../../components/Dialogs/AdditionalCoveragesDialog';
import { getDiffInYearsFromNow } from '../../helpers/dates';
import { getCountries, getStates } from '../../redux/modules/countries';
import WizardWrapper from '../../components/WizardWrapper';
import { EFES_CONTENT_WIDTH, FULL_WIDTH } from '../../constants/uiConstants';
import { loadAvailableDestinations } from '../../redux/modules/travelDestinations';
import { TRAVEL_PREFIX } from '../../constants/travelCalcConstants';
import { goToContractStep } from '../../helpers/routeHelper';
import { travelContractSignerSchema } from '../../validations/contract/travel';
import { useShowMobileContractLoader } from '../../hooks/useShowMobileContractLoader';
import { useClearAllNotifications } from '../../hooks/useClearAllNotifications';
import { handleContractSingerAction } from '../../helpers/contractHelpers';

import travelersIcon from '../../img/svgs/travelers.svg';
import baggageIcon from '../../img/svgs/baggage.svg';
import TravelContractHeaderCover from '../../img/travelHeader.png';

import summaryWrapperUseStyles from '../../components/MUI/SummaryWrapper/makeStyles';
import useStepNavigationAmountStyles from '../../components/MUI/StepNavigationAmount/makeStyles';

const getUnlockedStepsUpToPayment = (steps) => steps.filter((step) => step.n < 5).map((s) => s.id);

const TravelContractContainer = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const match = useRouteMatch();
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const token = urlSearchParams.get('t');

  useClearAllNotifications();

  const { elementIsFocused } = useSelector((state) => state.global);
  const summaryWrapperClasses = summaryWrapperUseStyles();
  const stepNavigationAmountClasses = useStepNavigationAmountStyles({ elementIsFocused });

  const goToStep = useCallback(
    (newStep) => goToContractStep(history, match, newStep),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, match.url],
  );

  const location = useLocation();
  const isDeviceLgSize = useDeviceResize('extraLgSize');
  const { loadingMobileContractData } = useSelector((state) => state.travelCalculation);

  const [urlParams] = useState(queryString.parse(location.hash));
  const [currentStep, setCurrentStep] = useState(contractSteps[0]);
  const [openTravelersModal, setOpenTravelersModal] = useState(false);
  const [openMobileSummary, setOpenMobileSummary] = useState(false);
  const [openAdditionalCoveragesModal, setOpenAdditionalCoveragesModal] = useState(false);
  const [stepNavigationBox, setStepNavigationBox] = useState({});
  const [unlockedSteps, setUnlockedSteps] = useState(
    urlParams.bfp === 'true' ? getUnlockedStepsUpToPayment(contractSteps) : [contractSteps[0].id],
  );
  const [isMemberFormOpen, setIsMemberFormOpen] = useState(false);
  const [showMobileContractDataLoader, setShowMobileContractDataLoader] = useState(!!token);

  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const suggestionsList = useSelector((state) => selectSuggestions(state));

  const paymentMethod = useSelector((store) => store.travelContract.paymentMethod);
  const orderId = useSelector((store) => store.travelContract.orderId);
  const draftToken = useSelector((store) => store.travelContract.draftToken);

  const contractData = useSelector((state) => state.travelContract.contractData, shallowEqual);
  const { promoCodes, travelers, travelDetails } = contractData;
  const {
    birthday,
    region,
    days,
    selectedLimit,
    selectedPlan,
    selectedCoverages,
    range,
    isFetchingPrice,
    prices,
    policyPrices,
    selectedDestinations,
  } = travelDetails;

  const loadingDraftContract = useSelector((store) => store.travelContract.loadingDraftContract);
  const savingContractInCache = useSelector((store) => store.travelContract.savingContractInCache);
  const allDestinations = useSelector(
    (store) => store.travelDestinations.allDestinations,
    shallowEqual,
  );
  const signerBackendErrors = useSelector(
    (state) => state.travelContract.signerBackendErrors,
    shallowEqual,
  );
  const travelerBackendErrors = useSelector(
    (state) => state.travelContract.travelerBackendErrors,
    shallowEqual,
  );
  const daysWithDebounce = useDebounce(days, TRAVEL_CONTRACT_DAYS_DEBOUNCE);
  const rangeWithDebounce = useDebounce(range, TRAVEL_CONTRACT_DAYS_DEBOUNCE);

  const showPromoCodes = useMemo(() => currentStep.id === stepsId.calculator, [currentStep]);
  const selectedDestinationRegion = useMemo(
    () => getSelectedRegionForSummaryContract(selectedDestinations, region),
    [selectedDestinations, region],
  );
  const handleSignerActionsBeforeNext = useCallback(
    (data) => {
      handleContractSingerAction(
        dispatch,
        setOrUpdateSigner,
        clearSignerBackendErrors,
        isAuthenticated,
        saveDraftContract,
        data,
      );
    },
    [dispatch, isAuthenticated],
  );

  const handleOpenMobileSummary = useCallback(() => {
    setOpenMobileSummary(!openMobileSummary);
  }, [openMobileSummary]);

  const handleTravelerActionsBeforeNext = useCallback(() => {
    if (isAuthenticated) {
      dispatch(saveDraftContract());
    }
  }, [dispatch, isAuthenticated]);

  const beforeNextMapping = {
    signer: handleSignerActionsBeforeNext,
    traveler: handleTravelerActionsBeforeNext,
  };

  const discountPrice = useMemo(() => getDiscountPrice(policyPrices, selectedPlan), [
    policyPrices,
    selectedPlan,
  ]);

  const discountedPrice = useMemo(() => getDiscountedPrice(policyPrices, selectedPlan), [
    policyPrices,
    selectedPlan,
  ]);

  const handleOpenTravelersModal = useCallback(() => {
    setOpenMobileSummary(false);
    setOpenTravelersModal(true);
  }, []);

  const handleWhenHasNoSigner = (callback) => {
    callback();
  };

  const memberStepParams = {
    memberType: MEMBER_TYPES.TRAVELER,
    members: travelers,
    memberBackendErrors: travelerBackendErrors,
    suggestionsList,
    memberIcon: baggageIcon,
    memberExistsIcon: travelersIcon,
    saveMember: saveTraveler,
    addMember: addTraveler,
    removeMember: removeTraveler,
    removeMemberBackendErrors: removeTravelerBackendErrors,
    setHasAddedSigner,
    isMemberFormOpen,
    setIsMemberFormOpen,
    handleWhenHasNoSigner,
  };

  useShowMobileContractLoader(loadingMobileContractData, setShowMobileContractDataLoader);

  useEffect(() => {
    dispatch(setPromoCodeData(travelers));
  }, [dispatch, travelers]);

  useEffect(() => {
    if (isDeviceLgSize) {
      setOpenAdditionalCoveragesModal(false);
    }
  }, [isDeviceLgSize]);

  useEffect(() => {
    dispatch(getCountries());
    dispatch(getStates());
    dispatch(loadAvailableDestinations());
  }, [dispatch]);

  useEffect(() => {
    if (!travelers.length) {
      dispatch(clearPrices(ACTION_NAME.CONTRACT));
    } else if (!isMemberFormOpen) {
      // Note: we check isMemberFormOpen to avoid sending request for every traveler Input change
      dispatch(
        getPrices(
          {
            age: null,
            travelers,
            region,
            days: daysWithDebounce,
            range: rangeWithDebounce,
            selectedLimit,
            selectedCoverages,
            promoCodes,
            selectedDestinations,
            selectedPlan,
          },
          ACTION_NAME.CONTRACT,
        ),
      );
    }
  }, [
    travelers,
    region,
    daysWithDebounce,
    rangeWithDebounce,
    selectedLimit,
    selectedCoverages,
    promoCodes,
    dispatch,
    isMemberFormOpen,
    selectedDestinations,
    selectedPlan,
  ]);

  useEffect(
    () => () => {
      dispatch(clearCalculation(ACTION_NAME.CONTRACT));
      dispatch(clearTravelContract());
    },
    [dispatch],
  );

  useEffect(() => {
    if (token && allDestinations.length) {
      dispatch(getMobileContractData(token, TRAVEL_PREFIX));
    }
  }, [dispatch, token, allDestinations]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getSuggestions());
      dispatch(getSavedCards());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (urlParams.bfp === 'true') {
      dispatch(getDraftTravelContractAndSaveInCache(urlParams.orderId, urlParams.draftToken))
        .then(() => {
          dispatch(setPaymentMethod(urlParams.paymentType));
          dispatch(setHasAgreed(true));
          window.location.hash = '';
        })
        .catch(() => {
          setUnlockedSteps([contractSteps[0].id]);
        });
    }
  }, [urlParams, dispatch]);

  useEffect(() => {
    if (signerBackendErrors) {
      goToStep(contractSteps[0]);
    } else if (travelerBackendErrors) {
      goToStep(contractSteps[1]);
    }
  }, [signerBackendErrors, travelerBackendErrors, goToStep]);

  const contractSummary = useMemo(
    () => getContractSummary(
      prices,
      t,
      selectedPlan,
      discountPrice,
      isFetchingPrice,
      contractData.travelers,
      i18n.language,
      summaryWrapperClasses,
      handleOpenTravelersModal,
    ),
    [
      prices,
      t,
      selectedPlan,
      discountPrice,
      contractData.travelers,
      isFetchingPrice,
      i18n.language,
      summaryWrapperClasses,
      handleOpenTravelersModal,
    ],
  );

  const hasOnlyLiteDisallowedCountry = useMemo(
    () => getOnlyLiteDisallowedCountry(selectedDestinations), [selectedDestinations],
  );

  const plansWithAllowedLimitRegionAndAge = useMemo(() => {
    const age = getDiffInYearsFromNow(birthday);
    return getPlansWithAllowedLimitRegionAndAge(region, age, hasOnlyLiteDisallowedCountry);
  }, [birthday, region, hasOnlyLiteDisallowedCountry]);

  const selectedPlansWithAllowedLimitRegionAndAge = useMemo(
    () => getSelectedPlansWithAllowedLimitRegionAndAge(
      plansWithAllowedLimitRegionAndAge, selectedPlan,
    ),
    [plansWithAllowedLimitRegionAndAge, selectedPlan],
  );

  const activeCoveragesCount = useMemo(() => getActiveCoveragesCount(prices, selectedPlan), [
    prices,
    selectedPlan,
  ]);

  const stepParams = {
    type: CONTRACT_TYPES.TRAVEL,
    data: contractData,
    ...memberStepParams,
    totalPrice: discountedPrice,
    isFetchingPrice,
    signerBackendErrors,
    handleActionsBeforeNext: beforeNextMapping[currentStep.id],
    loader: <Loader />,
    showLoader: loadingDraftContract || savingContractInCache,
    showMobileSummary: isFetchingPrice || !isEmpty(prices),
    actionName: ACTION_NAME.CONTRACT,
    handleOpenMobileSummary,
    isDeviceLgSize,
    paymentParams: {
      setPaymentMethod,
      paymentMethod,
      orderId,
      draftToken,
    },
    setStepNavigationBox,
    contractSignerSchema: travelContractSignerSchema,
  };

  if (showMobileContractDataLoader) {
    return <Loader />;
  }

  return (
    <>
      <TravelersDialog
        travelers={contractData.travelers}
        open={openTravelersModal}
        onClose={() => {
          setOpenTravelersModal(false);
          setOpenMobileSummary(true);
        }}
      />
      <AdditionalCoveragesDialog
        selectedPlansWithAllowedLimitRegionAndAge={selectedPlansWithAllowedLimitRegionAndAge}
        selectedPlan={selectedPlan}
        open={openAdditionalCoveragesModal}
        onClose={() => setOpenAdditionalCoveragesModal(false)}
      />
      <EfesContentHeader
        headerText="travelContract.title"
        headerImage={TravelContractHeaderCover}
      />

      <WizardWrapper
        steps={contractSteps}
        stepParams={stepParams}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        unlockedSteps={unlockedSteps}
        setUnlockedSteps={setUnlockedSteps}
        goToStep={goToStep}
        hasWarningModal
        locationsWhitelist={[TRAVEL_CONTRACT_SIGN_PATH]}
        contractSummaryClassName={summaryWrapperClasses.summaryWrapperBox}
        summaryBlock={(
          <ContractSummary
            amount={discountedPrice}
            days={travelDetails.days}
            contractSummary={contractSummary}
            openMobileSummary={openMobileSummary}
            onDismiss={() => setOpenMobileSummary(!openMobileSummary)}
            selectedLimit={selectedLimit}
            selectedPlan={selectedPlan}
            selectedPlansWithAllowedLimitRegionAndAge={selectedPlansWithAllowedLimitRegionAndAge}
            range={range}
            region={selectedDestinationRegion || ''}
            activeCoveragesCount={activeCoveragesCount}
            handleOpenAdditionalCoveragesModal={() => setOpenAdditionalCoveragesModal(true)}
            contractData={contractData}
            showPromoCodes={showPromoCodes}
            isFetchingPrice={isFetchingPrice}
          />
        )}
      />
      <Grid
        container
        className={cn({
          [stepNavigationAmountClasses.mobileNavigation]: isDeviceLgSize,
        })}
        spacing={isDeviceLgSize ? 4 : 0}
      >
        <Grid
          item
          style={{
            width: isDeviceLgSize ? FULL_WIDTH : EFES_CONTENT_WIDTH,
            paddingRight: !isDeviceLgSize && 20,
          }}
        >
          <Box>{!isEmpty(stepNavigationBox) && stepNavigationBox}</Box>
        </Grid>
      </Grid>
    </>
  );
};

export default TravelContractContainer;
